/**
 * Utility functions for handling cookie access with modern browser privacy features
 */
import { API_URL, FRONTEND_URL } from '../config';

/**
 * Detect if we're in a cross-origin context by comparing API_URL and current window location
 * 
 * Handles two deployment scenarios:
 * - Dev: Frontend on gym-dev.birlaventures.com, Backend on gym-dev.birlaventures.com/api (same-origin)
 * - Prod: Frontend on pumpgym.in, Backend on birlaventures.com/api (cross-origin)
 */
const isCrossOriginContext = () => {
  try {
    // Get the current origin (domain + protocol + port)
    const currentOrigin = window.location.origin;
    
    // Parse the API URL to get its origin
    const apiUrlObj = new URL(API_URL);
    const apiOrigin = apiUrlObj.origin;
    
    // Extract domains (remove protocol and port for comparison)
    const currentDomain = window.location.hostname;
    const apiDomain = apiUrlObj.hostname;
    
    // Check if the domains are different (cross-origin)
    // Same domain = same-origin (e.g., gym-dev.birlaventures.com vs gym-dev.birlaventures.com/api)
    // Different domain = cross-origin (e.g., pumpgym.in vs birlaventures.com)
    const isCrossOrigin = currentDomain.toLowerCase() !== apiDomain.toLowerCase();
    
    if (process.env.NODE_ENV === 'development') {
      console.log('Origin detection:', {
        currentOrigin,
        apiOrigin,
        currentDomain,
        apiDomain,
        isCrossOrigin
      });
    }
    
    return isCrossOrigin;
  } catch (error) {
    console.error('Error checking cross-origin context:', error);
    // Default to true (cross-origin) to be safe
    return true;
  }
};

/**
 * Detect third-party cookie blocking and other cookie access issues
 * Returns a user-friendly message if issues are detected
 * Improved for incognito mode compatibility
 */
export const detectCookieIssues = () => {
  try {
    // In incognito mode, cookies might work but with restrictions
    // Test with SameSite=Lax first (works better in incognito)
    const testValue = `test-${Date.now()}`;
    
    // Try setting a cookie with SameSite=Lax (works in incognito for same-origin)
    document.cookie = `cookieTest=${testValue}; path=/; SameSite=Lax; max-age=60`;
    
    // Try to read it back
    let hasCookie = document.cookie.indexOf(`cookieTest=${testValue}`) !== -1;
    
    // If that didn't work, try without SameSite (for older browsers)
    if (!hasCookie) {
      document.cookie = `cookieTest=${testValue}; path=/; max-age=60`;
      hasCookie = document.cookie.indexOf(`cookieTest=${testValue}`) !== -1;
    }
    
    // Clean up the test cookie
    document.cookie = `cookieTest=${testValue}; path=/; max-age=0`;
    
    // In incognito mode, even if cookies work, they might be restricted
    // So we check if localStorage works as a fallback
    let localStorageWorks = false;
    try {
      localStorage.setItem('_cookieTest', '1');
      localStorageWorks = localStorage.getItem('_cookieTest') === '1';
      localStorage.removeItem('_cookieTest');
    } catch (e) {
      // localStorage might be disabled in some incognito modes
    }
    
    // Only report an issue if cookies don't work AND localStorage doesn't work
    // This prevents false positives in incognito mode where localStorage works
    if (!hasCookie && !localStorageWorks) {
      // Check if we're in a cross-origin context
      const crossOrigin = isCrossOriginContext();
      
      if (crossOrigin) {
        return {
          hasIssue: true,
          message: "Third-party cookies appear to be blocked. Your browser is preventing cookies between our website and authentication server.",
          instructions: {
            chrome: "Settings > Privacy and Security > Cookies > Allow all cookies or Site Settings > Cookies > Allow",
            firefox: "Settings > Privacy & Security > Cookies and Site Data > Cross-site tracking cookies",
            safari: "Preferences > Privacy > Website tracking > uncheck 'Prevent cross-site tracking'",
            edge: "Settings > Cookies and site permissions > Manage and delete cookies > Allow",
          },
          crossOrigin: true,
          apiUrl: API_URL,
          frontendUrl: FRONTEND_URL || window.location.origin
        };
      } else {
        return {
          hasIssue: true,
          message: "Cookies appear to be blocked by your browser. Please enable cookies for this site to enable login functionality."
        };
      }
    }
    
    // If cookies don't work but localStorage does, that's okay - we can use token-based auth
    // Don't report it as an issue since we have a fallback
    return { hasIssue: false };
  } catch (error) {
    console.error('Error detecting cookie issues:', error);
    // Don't report as issue if we can't detect - let the auth flow handle it
    return { 
      hasIssue: false,  // Changed to false to prevent false positives
      message: "Unable to verify cookie access. Using token-based authentication as fallback." 
    };
  }
};

/**
 * Modern approach to handling cookies in a first-party context
 * 
 * @returns {Promise<boolean>} Always returns true to continue auth flow
 */
export const requestStorageAccess = async () => {
  // For first-party contexts, we simply use SameSite=Lax cookies
  // which are supported in all modern browsers
  
  // Set a test cookie to verify access
  document.cookie = "cookieAccess=1; path=/; SameSite=Lax; max-age=60";
  
  // Return true to continue the auth flow regardless of the outcome
  return true;
};

/**
 * Simplified approach to ensure we can access cookies for authentication
 * Call this before making authentication requests
 */
export const ensureCookieAccess = async () => {
  try {
    // Simple cookie test - just check if we can set and read a basic cookie
    const testValue = `access-${Date.now()}`;
    
    // Set a simple test cookie with minimal flags
    document.cookie = `cookieAccess=${testValue}; path=/; max-age=60`;
    
    // Verify the cookie was actually set
    const cookieSet = document.cookie.indexOf(`cookieAccess=${testValue}`) !== -1;
    
    // Clean up the test cookie
    document.cookie = `cookieAccess=${testValue}; path=/; max-age=0`;
    
    if (!cookieSet) {
      console.warn('Cookies appear to be blocked by the browser. Falling back to localStorage for session management.');
      // Use localStorage as a fallback for session management
      try {
        localStorage.setItem('_localAuthFlag', '1');
        if (localStorage.getItem('_localAuthFlag') === '1') {
          // localStorage works, we can use it as a cookie alternative
          return true;
        }
      } catch (storageErr) {
        console.error('Both cookies and localStorage are unavailable:', storageErr);
      }
      // Return true to maintain previous behavior of continuing auth flow
      return true;
    }
    
    return true; // Continue with the auth flow
  } catch (error) {
    console.warn('Issue in ensureCookieAccess:', error);
    // Return true to maintain compatibility with previous behavior
    return true;
  }
};

/**
 * Get CSRF token from a cookie if available
 * This can be used for additional security with API requests
 */
export const getCSRFToken = () => {
  try {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.indexOf('csrf_token=') === 0) {
        return cookie.substring('csrf_token='.length, cookie.length);
      }
    }
  } catch (error) {
    console.error('Error getting CSRF token:', error);
  }
  return null;
};

/**
 * Check if cookies are accessible
 * This can be used to detect if cookies are blocked
 */
export const checkCookieAccess = () => {
  try {
    // Simple cookie test - just check if we can set and read a basic cookie
    const testValue = `check-${Date.now()}`;
    
    // Set a simple test cookie with minimal flags
    document.cookie = `cookieAccess=${testValue}; path=/; max-age=60`;
    
    // Check if the cookie was set
    const hasCookie = document.cookie.indexOf(`cookieAccess=${testValue}`) !== -1;
    
    // Clean up the test cookie
    document.cookie = `cookieAccess=${testValue}; path=/; max-age=0`;
    
    return hasCookie;
  } catch (error) {
    console.error('Error checking cookie access:', error);
    return false;
  }
};

/**
 * Get authentication status based on localStorage when cookies are blocked
 * This is a critical fallback for incognito mode and environments where third-party cookies are blocked
 */
export const getAuthStatusFromLocalStorage = () => {
  try {
    const cachedUser = localStorage.getItem("user");
    const userId = localStorage.getItem("user_id");
    const timestamp = localStorage.getItem("user_profile_timestamp");
    
    if (!cachedUser || !userId || !timestamp) {
      return { isAuthenticated: false };
    }
    
    // Check if the stored data is relatively recent (24 hours)
    const storedTime = parseInt(timestamp);
    const currentTime = Date.now();
    const MAX_AGE = 24 * 60 * 60 * 1000; // 24 hours
    
    if (isNaN(storedTime) || (currentTime - storedTime > MAX_AGE)) {
      return { isAuthenticated: false };
    }
    
    // If we have valid, recent user data in localStorage, consider the user authenticated
    const userData = JSON.parse(cachedUser);
    return { 
      isAuthenticated: true, 
      userData,
      fromLocalStorage: true
    };
  } catch (error) {
    console.error("Error getting auth status from localStorage:", error);
    return { isAuthenticated: false };
  }
};

/**
 * Unified approach to check authentication status from cookies or localStorage
 * This ensures authentication works even when cookies are blocked
 */
export const getAuthStatus = async () => {
  // First check if cookies are accessible
  const cookieStatus = detectCookieIssues();
  const cookiesAccessible = document.cookie.length > 0;
  
  // If cookies are blocked, fall back to localStorage
  if (cookieStatus.hasIssue || !cookiesAccessible) {
    console.warn("Cookies appear to be blocked. Falling back to localStorage for authentication.");
    return getAuthStatusFromLocalStorage();
  }
  
  // If cookies are accessible, still check localStorage as a fallback
  return getAuthStatusFromLocalStorage();
};

/**
 * Store user authentication data in localStorage
 * This is a critical function for ensuring auth works even when cookies are blocked
 */
export const storeAuthData = (userData) => {
  if (!userData || !userData.user_id) {
    console.error('Cannot store auth data: Invalid user data');
    return false;
  }
  
  try {
    // Store current timestamp for cache validation
    const currentTime = Date.now();
    
    // Store the essential user data
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('user_profile_timestamp', currentTime.toString());
    
    // Store auth_token separately if present (for token-based auth when cookies fail)
    if (userData.auth_token) {
      localStorage.setItem('auth_token', userData.auth_token);
      console.log('Auth token stored for token-based authentication');
    }
    
    // Set a flag indicating we're using localStorage auth
    localStorage.setItem('using_localstorage_auth', 'true');
    
    return true;
  } catch (error) {
    console.error('Error storing auth data in localStorage:', error);
    return false;
  }
};

/**
 * Clear authentication data from localStorage
 */
export const clearAuthData = () => {
  try {
    localStorage.removeItem('user');
    localStorage.removeItem('user_id');
    localStorage.removeItem('user_profile_timestamp');
    localStorage.removeItem('using_localstorage_auth');
    localStorage.removeItem('auth_token'); // Also clear the auth token
    return true;
  } catch (error) {
    console.error('Error clearing auth data from localStorage:', error);
    return false;
  }
};

/**
 * Clear all cookie-related issue flags from localStorage and sessionStorage
 * Call this function when cookies become accessible or when the user dismisses warnings
 */
export const clearCookieIssueFlags = () => {
  try {
    // Set cookie flags to 'false' to indicate that issues are resolved
    localStorage.setItem('cookie_issue', 'false');
    
    // Remove all other cookie-related issue flags
    localStorage.removeItem('cookie_auth_issue');
    localStorage.removeItem('cookie-warning-shown');
    sessionStorage.removeItem('cookie-warning-shown');
    
    // Also clear related flags that might be set due to cookie issues
    localStorage.removeItem('using_localstorage_auth');
    
    // Dispatch events to notify listening components
    window.dispatchEvent(new Event('storage'));
    window.dispatchEvent(new CustomEvent('cookie-issue-update', { 
      detail: { hasIssue: false }
    }));
    
    return true;
  } catch (error) {
    console.error('Error clearing cookie issue flags:', error);
    return false;
  }
};

/**
 * Set cookie issue flag to show the banner when needed
 * Call this function when login was successful but profile fetch failed
 * due to cookie issues
 */
export const setCookieIssueFlag = () => {
  try {
    // Set cookie_issue flag to true to trigger showing the banner
    localStorage.setItem('cookie_issue', 'true');
    localStorage.setItem('cookie_auth_issue', 'true');
    localStorage.setItem('cookie_issue_timestamp', Date.now().toString());
    
    // Dispatch a storage event to notify other components
    window.dispatchEvent(new Event('storage'));
    
    // Also dispatch a custom event for components listening specifically for cookie issues
    window.dispatchEvent(new CustomEvent('cookie-issue-update', { 
      detail: { hasIssue: true }
    }));
    
    return true;
  } catch (error) {
    console.error('Error setting cookie issue flag:', error);
    return false;
  }
};

/**
 * Check if profile fetch was successful after authentication
 * Returns true if profile was successfully fetched, false otherwise
 */
export const wasProfileFetchSuccessful = () => {
  try {
    // Check if we have profile data in localStorage
    const profileStats = localStorage.getItem('profileStats');
    const user = localStorage.getItem('user');
    
    // If both profile stats and user exist, consider profile fetch successful
    if (profileStats && user) {
      // Get timestamps to check recency
      const profileStatsTime = localStorage.getItem('profileStatsTimestamp');
      const userTime = localStorage.getItem('user_profile_timestamp');
      
      if (profileStatsTime && userTime) {
        const now = Date.now();
        const profileAge = now - parseInt(profileStatsTime);
        const userAge = now - parseInt(userTime);
        
        // Consider fetch successful if data is recent (less than 5 minutes old)
        const MAX_AGE = 5 * 60 * 1000; // 5 minutes
        if (profileAge < MAX_AGE && userAge < MAX_AGE) {
          return true;
        }
      }
    }
    
    return false;
  } catch (error) {
    console.error('Error checking if profile fetch was successful:', error);
    return false;
  }
};

/**
 * Debug function to check cookie status and provide detailed information
 * This can be called from browser console for troubleshooting
 */
export const debugCookieStatus = () => {
  const result = {
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    currentUrl: window.location.href,
    cookieString: document.cookie,
    cookieCount: document.cookie.split(';').filter(c => c.trim()).length,
    localStorageAvailable: !!window.localStorage,
    sessionStorageAvailable: !!window.sessionStorage,
    crossOrigin: isCrossOriginContext(),
    cookieTest: detectCookieIssues(),
    cookieAccess: checkCookieAccess()
  };
  
  console.log('Cookie Debug Information:', result);
  return result;
};

// Make it available globally for debugging
if (typeof window !== 'undefined') {
  window.debugCookieStatus = debugCookieStatus;
}